import { toast } from 'react-hot-toast';
import { useAuthQuery } from '#lib/hooks/use-query';
import { jsonHeaders } from '#lib/helpers/fetch';
import useAuth, { getAuthHeader } from '#lib/hooks/use-auth';
import useAsync from '#lib/hooks/use-async';
import { useMutation } from '@tanstack/react-query';
import { neutralPalettes, palettes } from '#lib/helpers/tailwind.js';
import { fetchAndParse } from '#lib/helpers/fetch.js';

async function fetchProfile(_, authHeaders) {
    const response = await fetch(`${API_URL}/p/profile`, {
        headers: { ...jsonHeaders, ...authHeaders }
    });
    return await response.json();
}

export function useProfile() {
    const { data, ...queryProps } = useAuthQuery({
        queryKey: ['profile'],
        queryFn: fetchProfile,
        staleTime: 0,
        cacheTime: 0,
        refetchOnWindowFocus: false,
    });

    return {
        data: data?.results,
        ...queryProps
    };
}
async function save(data) {
    const headers = await getAuthHeader();
    const response = await fetchAndParse(`${API_URL}/p/profile`, {
        method: 'POST',
        headers: {
            ...jsonHeaders,
            ...headers
        },
        body: JSON.stringify(data)
    });
    return response;
}

async function saveBillingInfo(data) {
    const headers = await getAuthHeader();
    const response = await fetchAndParse(`${API_URL}/p/billinginfo`, {
        method: 'POST',
        headers: {
            ...jsonHeaders,
            ...headers
        },
        body: JSON.stringify(data)
    });
    return response;
}

export function useSaveProfile(options) {
    return useMutation({
        mutationFn: save,
        ...options
    });
}

export function useSaveBillingInfo(options) {
    return useMutation({
        mutationFn: saveBillingInfo,
        ...options
    });
}

async function editProfileMutation(data) {
    const headers = await getAuthHeader();
    const response = await fetch(`${API_URL}/p/profile/edit`, {
        method: 'PUT',
        headers: {
            ...jsonHeaders,
            ...headers
        },
        body: JSON.stringify(data)
    });
    return await response.json();
}

export function useEditProfile(options) {
    return useMutation({
        mutationFn: editProfileMutation,
        onSuccess: () => {
            toast.success('Profilo aggiornato!');
        },
        onError: (error) => {
            toast.error(error.message || "Si è verificato un errore durante l'aggiornamento del profilo.");
        },
        ...options
    });
}

export function useChangeImageProfile(options) {
    return useAsync(async (tempkey) => {
        const headers = await getAuthHeader();
        const response = await fetch(`${API_URL}/p/profile/update-image`, {
            method: 'PUT',
            headers: {
                ...jsonHeaders,
                ...headers
            },
            body: JSON.stringify({ immagineprofilo: tempkey })
        });

        if (response.status === 200) {
            toast.success('Immagine aggiornata!');
        } else {
            toast.error(response.statusText);
        }
    }, options);
}

async function deleteProfile(data) {
    const headers = await getAuthHeader();
    const response = await fetch(`${API_URL}/p/profile`, {
        method: 'DELETE',
        headers: { ...jsonHeaders, ...headers },
        body: JSON.stringify(data)
    });
    return response;
}

export function useDeleteProfile(options) {
    const deleteMutation = useMutation({
        mutationFn: deleteProfile,
        onSuccess: () => {
            toast.success('Profile deleted!');
        },
        onError: (error) => {
            toast.error(error.message || 'An error occurred while deleting the profile.');
        },
        ...options
    });

    return {
        ...deleteMutation
    };
}

async function saveApiKeys() {
    const headers = await getAuthHeader();
    const response = await fetch(`${API_URL}/p/profile/keys`, {
        method: 'POST',
        headers: {
            ...jsonHeaders,
            ...headers
        },
        body: JSON.stringify()
    });

    return await response.json();
}

export function useSaveApiKeys(options) {
    return useMutation({
        mutationFn: saveApiKeys,
        ...options
    });
}

async function deleteApiKeys() {
    const headers = await getAuthHeader();
    const response = await fetch(`${API_URL}/p/profile/keys`, {
        method: 'DELETE',
        headers: { ...jsonHeaders, ...headers },
        body: JSON.stringify()
    });
    return await response.json();
}

export function useDeleteApiKeys(options) {
    const deleteMutation = useMutation({
        mutationFn: deleteApiKeys,
        onSuccess: () => {
            toast.success('API keys successfully deleted!');
        },
        onError: (error) => {
            toast.error(error.message || 'An error occurred while deleting the API keys.');
        },
        ...options
    });

    return {
        ...deleteMutation
    };
}
